/**
 * Interactive inline chip for Strong's numbers in chat text.
 *
 * Renders as `native (pronunciation)` — e.g. `ἀγαπάω (agapaō)` — once the
 * Strong's metadata is available; falls back to the raw `displayText` (the
 * H#### / G#### form the parser captured) while data is loading or if the
 * fetch fails. Brian's call (2026-05-12) — "seeing the native Greek and
 * Hebrew characters adds delight to the response."
 *
 * Hover → popover with definition preview.
 * Click → opens full StrongsPanel bottom drawer.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import type { StrongsRef } from '@skjv/common';
import { fetchStrongsDefinition } from '@api/strongs';
import { definitionPlainText } from '@components/strongs-panel/definition-plain-text';
import { bibleState } from '@/state/bible-state';
import { StrongsPopover } from './strongs-popover';

interface InlineStrongsChipProps {
  number: string;
  displayText: string;
  preloaded?: StrongsRef;
}

export function InlineStrongsChip({ number, displayText, preloaded }: InlineStrongsChipProps) {
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [data, setData] = useState<StrongsRef | null>(preloaded ?? null);
  const chipRef = useRef<HTMLButtonElement>(null);
  const hasFetchedRef = useRef(!!preloaded);

  const fetchDef = useCallback(async () => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    try {
      const def = await fetchStrongsDefinition(number);
      if (def) {
        setData({
          number,
          native: def.metadata.native,
          pronunciation: def.metadata.pronunciation,
          shortDefinition: definitionPlainText(def.metadata.definition).substring(0, 120),
          language: def.metadata.language as 'Hebrew' | 'Greek',
        });
      }
    } catch {
      // Failed to fetch — chip just keeps the displayText fallback
    }
  }, [number]);

  // Eager fetch on mount so the chip renders `native (pronunciation)` from
  // first paint when possible. Cheap — the Strong's API is fast + cached,
  // and a chat message usually has only 1-3 chips.
  useEffect(() => {
    fetchDef();
  }, [fetchDef]);

  const handleClick = useCallback(() => {
    setIsPopoverVisible(false);
    bibleState.showStrongsDefinition(number, bibleState.currentLocation);
  }, [number]);

  const richText = data?.native && data?.pronunciation
    ? `${data.native} (${data.pronunciation})`
    : displayText;

  return (
    <>
      <button
        ref={chipRef}
        className="strongs-chip"
        onMouseEnter={() => setIsPopoverVisible(true)}
        onMouseLeave={() => setIsPopoverVisible(false)}
        onClick={handleClick}
      >
        {richText}
      </button>
      {isPopoverVisible && data && (
        <StrongsPopover data={data} anchorRef={chipRef} />
      )}
    </>
  );
}
